import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Alert } from '@mui/material';
import Snackbar from '@mui/material/Snackbar';

const sixDigit = /^\d{6}$/;

const Security2FA = () => {
    const [qrImage, setQrImage] = useState('');
    const [enableToken, setEnableToken] = useState('');
    const [disableToken, setDisableToken] = useState('');
    const [mfaEnabled, setMfaEnabled] = useState(false);
    const [loadingStatus, setLoadingStatus] = useState(true);
    const [loadingGenerate, setLoadingGenerate] = useState(false);
    const [loadingEnable, setLoadingEnable] = useState(false);
    const [loadingDisable, setLoadingDisable] = useState(false);
    const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });

    const authHeader = () => {
        const token = localStorage.getItem('adminAuthToken');
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    const showSnack = (message, severity = 'error') => {
        setSnack({ open: true, severity, message });
    };

    const parseError = (error) => {
        const status = error?.response?.status;
        const message = error?.response?.data?.message || error?.response?.data?.error || 'Request failed';
        if (status === 429) return 'Too many requests. Please retry after some time.';
        if (status === 401) return 'Unauthorized. Please login again.';
        return message;
    };

    const load2FAStatus = async () => {
        setLoadingStatus(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_KEY}api/2fa/status`,
                { headers: authHeader() }
            );
            const enabled = Boolean(response?.data?.mfa_enabled);
            setMfaEnabled(enabled);

            if (!enabled) {
                await generate2FA();
            } else {
                setQrImage('');
            }
        } catch (error) {
            showSnack(parseError(error));
        } finally {
            setLoadingStatus(false);
        }
    };

    const generate2FA = async () => {
        setLoadingGenerate(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_KEY}api/2fa/generate`,
                {},
                { headers: authHeader() }
            );
            if (!response?.data?.qrImage) {
                showSnack('QR image was not returned by the server.');
                return;
            }
            setQrImage(response.data.qrImage);
            showSnack('QR generated. Scan it and enter a 6-digit code to activate.', 'success');
        } catch (error) {
            showSnack(parseError(error));
        } finally {
            setLoadingGenerate(false);
        }
    };

    const activate2FA = async (e) => {
        e.preventDefault();
        if (!sixDigit.test(enableToken)) {
            showSnack('Enter a valid 6-digit code to activate 2FA.');
            return;
        }
        setLoadingEnable(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_KEY}api/2fa/activate`,
                { token: enableToken },
                { headers: authHeader() }
            );
            showSnack(response?.data?.message || '2FA activated successfully.', 'success');
            setMfaEnabled(true);
            setQrImage('');
            setEnableToken('');
        } catch (error) {
            showSnack(parseError(error));
        } finally {
            setLoadingEnable(false);
        }
    };

    const disable2FA = async (e) => {
        e.preventDefault();
        if (!sixDigit.test(disableToken)) {
            showSnack('Enter a valid 6-digit code to disable 2FA.');
            return;
        }
        setLoadingDisable(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_KEY}api/2fa/disable`,
                { token: disableToken },
                { headers: authHeader() }
            );
            showSnack(response?.data?.message || '2FA disabled successfully.', 'success');
            setMfaEnabled(false);
            setDisableToken('');
            await generate2FA();
        } catch (error) {
            showSnack(parseError(error));
        } finally {
            setLoadingDisable(false);
        }
    };

    useEffect(() => {
        load2FAStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className='container mt-4 mb-5'>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={snack.open}
                autoHideDuration={3500}
                onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
            >
                <Alert onClose={() => setSnack((prev) => ({ ...prev, open: false }))} severity={snack.severity} sx={{ width: '100%' }}>
                    {snack.message}
                </Alert>
            </Snackbar>

            <div className='card p-4 shadow-sm'>
                <h2 className='mb-2'>Security Settings</h2>
                <p className='mb-4'>Manage Two-Factor Authentication (Google Authenticator / Microsoft Authenticator).</p>

                {loadingStatus ? (
                    <div className='border rounded p-3'>
                        <p className='mb-0'>Loading 2FA status...</p>
                    </div>
                ) : !mfaEnabled ? (
                    <div className='border rounded p-3 mb-4'>
                        <h4 className='mb-3'>Set up 2FA</h4>
                        <p className='mb-2'>2FA is not enabled. Scan this QR and validate with TOTP.</p>
                        <button className='btn btn-primary mb-3' onClick={generate2FA} disabled={loadingGenerate}>
                            {loadingGenerate ? 'Generating...' : 'Regenerate QR'}
                        </button>

                        {qrImage ? (
                            <div className='mb-3'>
                                <img src={qrImage} alt='2FA QR' style={{ maxWidth: 240, width: '100%' }} />
                            </div>
                        ) : null}

                        <form onSubmit={activate2FA}>
                            <div className='form-group'>
                                <label htmlFor='enable-2fa-token'>6-digit code</label>
                                <input
                                    id='enable-2fa-token'
                                    className='form-control'
                                    inputMode='numeric'
                                    maxLength={6}
                                    placeholder='123456'
                                    value={enableToken}
                                    onChange={(e) => setEnableToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    disabled={loadingEnable}
                                />
                            </div>
                            <button className='btn btn-success mt-3' type='submit' disabled={loadingEnable || !sixDigit.test(enableToken)}>
                                {loadingEnable ? 'Activating...' : 'Activate 2FA'}
                            </button>
                        </form>
                    </div>
                ) : (
                    <div className='border rounded p-3'>
                        <h4 className='mb-3'>2FA Enabled</h4>
                        <p className='mb-2'>Enter your current authenticator TOTP to validate and disable 2FA.</p>
                        <form onSubmit={disable2FA}>
                            <div className='form-group'>
                                <label htmlFor='disable-2fa-token'>6-digit code</label>
                                <input
                                    id='disable-2fa-token'
                                    className='form-control'
                                    inputMode='numeric'
                                    maxLength={6}
                                    placeholder='123456'
                                    value={disableToken}
                                    onChange={(e) => setDisableToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    disabled={loadingDisable}
                                />
                            </div>
                            <button className='btn btn-danger mt-3' type='submit' disabled={loadingDisable || !sixDigit.test(disableToken)}>
                                {loadingDisable ? 'Disabling...' : 'Disable 2FA'}
                            </button>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Security2FA;
